import { createTheme, type ThemeOptions } from "@mui/material/styles";
import { frFR as coreFR } from "@mui/material/locale";
import { frFR as gridFR } from "@mui/x-data-grid/locales";
import { frFR as pickersFR } from "@mui/x-date-pickers/locales";
import { tokens } from "./tokens";

export type ThemeMode = "light" | "dark";

const baseOptions: ThemeOptions = {
  typography: {
    fontFamily: tokens.font.family.sans,
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: {
    borderRadius: tokens.radius.md,
  },
  breakpoints: {
    values: tokens.breakpoint,
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: tokens.radius.md },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0, variant: "outlined" },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "transparent" },
    },
  },
};

function isValidHex(c: string | null | undefined): c is string {
  return Boolean(c && /^#[0-9a-fA-F]{6}$/.test(c));
}

export function buildTheme(mode: ThemeMode, primaryOverride?: string | null) {
  const usePrimaryOverride = isValidHex(primaryOverride);
  const palette =
    mode === "light"
      ? {
          mode: "light" as const,
          primary: usePrimaryOverride
            ? { main: primaryOverride }
            : { main: tokens.color.brand.primary, light: "#5B89FF", dark: "#1B4FD1" },
          secondary: { main: tokens.color.brand.accentPurple },
          success: { main: tokens.color.brand.success },
          warning: { main: tokens.color.brand.warning },
          error: { main: tokens.color.brand.danger },
          info: { main: tokens.color.brand.info },
          background: {
            default: tokens.color.light.background,
            paper: tokens.color.light.surface,
          },
          text: {
            primary: tokens.color.light.text,
            secondary: tokens.color.light.textMuted,
          },
          divider: tokens.color.light.border,
        }
      : {
          mode: "dark" as const,
          primary: usePrimaryOverride
            ? { main: primaryOverride }
            : { main: "#5B89FF", light: "#85A6FF", dark: "#2563FF" },
          secondary: { main: tokens.color.brand.accentPurple },
          success: { main: "#81c784" },
          warning: { main: "#ffb74d" },
          error: { main: "#e57373" },
          info: { main: "#64b5f6" },
          background: {
            default: tokens.color.dark.background,
            paper: tokens.color.dark.surface,
          },
          text: {
            primary: tokens.color.dark.text,
            secondary: tokens.color.dark.textMuted,
          },
          divider: tokens.color.dark.divider,
          grey: {
            50:  "#fafafa",
            100: "#f5f5f5",
            200: "#eeeeee",
            300: "#e0e0e0",
            400: "#bdbdbd",
            500: "#9e9e9e",
            600: "#757575",
            700: "#616161",
            800: "#424242",
            900: "#212121",
          },
        };

  return createTheme(
    { ...baseOptions, palette },
    coreFR,
    gridFR,
    pickersFR,
  );
}
